import { useEffect, useMemo, useState } from "react";
import { errorMessage } from "../api/client";
import {
  formatBrl,
  useAppTasks,
  useEnvironmentTypes,
  useTaskCategories,
  useUpdateAppTask,
} from "../api/appTasks";
import type { AppTaskRead, EnvironmentTypeRead, TaskCategoryRead } from "../api/types";
import { formatDate } from "../lib/format";
import AppTaskCreateDialog from "../components/AppTaskCreateDialog";
import AppTaskDrawer from "../components/AppTaskDrawer";
import { CategoryPill, StarButton, Switch, envColor } from "../components/AppTaskControls";
import TaskCategoriesDialog from "../components/TaskCategoriesDialog";

// App Tasks (master only): the catalog the mobile app lists, split by environment
// (Residential / Commercial) and grouped by category. Inline star = highlight, inline switch =
// active; everything else through the edit drawer. Categories are managed in their own dialog.

function useToast(): [string | null, (m: string) => void] {
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(null), 2600);
    return () => clearTimeout(t);
  }, [msg]);
  return [msg, setMsg];
}

function EnvStat({
  env,
  tasks,
}: {
  env: EnvironmentTypeRead;
  tasks: AppTaskRead[];
}) {
  const active = tasks.filter((t) => t.is_active);
  const highlighted = active.filter((t) => t.is_featured).length;
  const disabled = tasks.length - active.length;
  const rated = active.filter((t) => t.payments_value != null);
  const avg = rated.length
    ? rated.reduce((s, t) => s + (t.payments_value ?? 0), 0) / rated.length
    : null;
  const unrated = active.length - rated.length;
  return (
    <div className="card at-stat" style={{ ["--sc" as string]: envColor(env.slug) }}>
      <div className="at-stat-env">
        <b>{env.name_en}</b>
        <span>{env.name}</span>
      </div>
      <div className="at-stat-nums">
        <div>
          <b>{active.length}</b>
          <span>active</span>
        </div>
        <div className="hl">
          <b>{highlighted}</b>
          <span>highlighted</span>
        </div>
        <div className="off">
          <b>{disabled}</b>
          <span>disabled</span>
        </div>
        <div>
          <b>{avg == null ? "—" : formatBrl(avg)}</b>
          <span>avg / hour</span>
        </div>
        {unrated > 0 && (
          <div className="warn">
            <b>{unrated}</b>
            <span>no rate</span>
          </div>
        )}
      </div>
    </div>
  );
}

function TaskRow({
  task,
  category,
  onEdit,
  onToast,
}: {
  task: AppTaskRead;
  category: TaskCategoryRead | undefined;
  onEdit: () => void;
  onToast: (m: string) => void;
}) {
  const update = useUpdateAppTask();
  const cls = [task.is_active ? "" : "off", task.is_featured && task.is_active ? "hl" : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <tr className={cls || undefined}>
      <td className="at-col-star">
        <StarButton
          on={task.is_featured}
          disabled={update.isPending}
          onToggle={() =>
            update.mutate(
              { id: task.id, body: { is_featured: !task.is_featured } },
              {
                onSuccess: (saved) =>
                  onToast(
                    saved.is_featured
                      ? `“${saved.title}” highlighted in the app`
                      : `Highlight removed from “${saved.title}”`,
                  ),
              },
            )
          }
        />
      </td>
      <td>
        <div className="at-title">{task.title}</div>
        <div className="at-slug">
          {task.id}
          <span className="at-slug-en"> · {task.title_en}</span>
        </div>
      </td>
      <td>
        <CategoryPill cat={category} fallbackName={task.category_name} />
      </td>
      <td className="num">
        {task.payments_value == null ? (
          <button className="at-money unset" onClick={onEdit} title="Set the hourly rate">
            set rate
          </button>
        ) : (
          <span className="at-money">
            {formatBrl(task.payments_value)}
            <span className="at-per">/h</span>
          </span>
        )}
      </td>
      <td className="at-col-status">
        <span className="at-status">
          <Switch
            checked={task.is_active}
            disabled={update.isPending}
            label={`${task.title} active`}
            onChange={(next) =>
              update.mutate(
                { id: task.id, body: { is_active: next } },
                {
                  onSuccess: (saved) =>
                    onToast(
                      saved.is_active
                        ? `“${saved.title}” enabled`
                        : `“${saved.title}” disabled · last_disable_at stamped`,
                    ),
                },
              )
            }
          />
          <span className={task.is_active ? "at-status-lab on" : "at-status-lab"}>
            {task.is_active ? "Active" : "Disabled"}
          </span>
        </span>
        {update.isError && <div className="error small">{errorMessage(update.error)}</div>}
      </td>
      <td>
        <span className={task.last_disable_at ? "at-when" : "at-when none"}>
          {task.last_disable_at ? formatDate(task.last_disable_at) : "—"}
        </span>
      </td>
      <td className="at-col-act">
        <button className="btn ghost small" onClick={onEdit}>
          Edit
        </button>
      </td>
    </tr>
  );
}

export default function AppTasksPage() {
  const environments = useEnvironmentTypes();
  const categories = useTaskCategories();
  const tasks = useAppTasks();

  const [envSlug, setEnvSlug] = useState<string | null>(null);
  const [catFilter, setCatFilter] = useState<string | null>(null); // category id
  const [q, setQ] = useState("");
  const [showDisabled, setShowDisabled] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [managingCats, setManagingCats] = useState(false);
  const [toast, showToast] = useToast();

  const envs = environments.data ?? [];
  const cats = categories.data ?? [];
  const all = tasks.data ?? [];
  const env = envs.find((e) => e.slug === envSlug) ?? envs[0];
  const catById = useMemo(() => new Map(cats.map((c) => [c.id, c])), [cats]);

  const envCats = useMemo(
    () => cats.filter((c) => c.environment_slug === env?.slug),
    [cats, env?.slug],
  );
  const envTasks = useMemo(
    () => all.filter((t) => t.environment_slug === env?.slug),
    [all, env?.slug],
  );
  const visible = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return envTasks.filter(
      (t) =>
        (catFilter === null || t.task_category_id === catFilter) &&
        (showDisabled || t.is_active) &&
        (!qq ||
          t.title.toLowerCase().includes(qq) ||
          t.title_en.toLowerCase().includes(qq) ||
          t.id.includes(qq)),
    );
  }, [envTasks, catFilter, showDisabled, q]);

  // group by category in category order; highlighted first inside a group, then sort_order
  const groups = useMemo(
    () =>
      envCats
        .map((c) => ({
          cat: c,
          rows: visible
            .filter((t) => t.task_category_id === c.id)
            .sort((a, b) => Number(b.is_featured) - Number(a.is_featured) || a.sort_order - b.sort_order),
        }))
        .filter((g) => g.rows.length > 0),
    [envCats, visible],
  );

  const editing = editingId ? (all.find((t) => t.id === editingId) ?? null) : null;
  const existingIds = useMemo(() => new Set(all.map((t) => t.id)), [all]);
  const loading = environments.isLoading || categories.isLoading || tasks.isLoading;
  const error = environments.error ?? categories.error ?? tasks.error;

  return (
    <div className="app-tasks-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">App Tasks</h1>
          <p className="page-sub">
            The task catalog shown in the Android/iOS app. Set each task's environment, category,
            hourly rate and highlight; disable a task to hide it from the app.
          </p>
        </div>
        <div className="at-head-actions">
          <button className="btn ghost" onClick={() => setManagingCats(true)} disabled={!env}>
            Manage categories
          </button>
          <button className="btn primary" onClick={() => setCreating(true)} disabled={!env}>
            + New task
          </button>
        </div>
      </div>

      {loading && <p className="muted small">Loading…</p>}
      {error && <p className="error">Failed to load: {errorMessage(error)}</p>}

      {env && (
        <>
          <section className="at-stats">
            {envs.map((e) => (
              <EnvStat key={e.id} env={e} tasks={all.filter((t) => t.environment_slug === e.slug)} />
            ))}
          </section>

          <div className="at-seg" role="tablist" aria-label="Environment">
            {envs.map((e) => (
              <button
                key={e.id}
                role="tab"
                aria-selected={e.slug === env.slug}
                style={{ ["--sc" as string]: envColor(e.slug) }}
                onClick={() => {
                  setEnvSlug(e.slug);
                  setCatFilter(null);
                }}
              >
                {e.name_en}
                <span className="at-cnt">{all.filter((t) => t.environment_slug === e.slug).length}</span>
              </button>
            ))}
          </div>

          <div className="at-toolbar">
            <input
              className="text-input compact at-search"
              type="search"
              placeholder="Search task or id…"
              aria-label="Search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <div className="at-chips">
              <button
                className="at-chip"
                aria-pressed={catFilter === null}
                onClick={() => setCatFilter(null)}
              >
                All
              </button>
              {envCats.map((c) => {
                const n = envTasks.filter((t) => t.task_category_id === c.id).length;
                if (!n) return null;
                return (
                  <button
                    key={c.id}
                    className="at-chip"
                    aria-pressed={catFilter === c.id}
                    style={{ ["--sc" as string]: c.color ?? "#7c877f" }}
                    onClick={() => setCatFilter(catFilter === c.id ? null : c.id)}
                  >
                    {c.name} <span className="faint">{n}</span>
                  </button>
                );
              })}
            </div>
            <label className="at-toggle-label">
              <Switch checked={showDisabled} onChange={setShowDisabled} label="Show disabled" />
              Show disabled
            </label>
          </div>

          <section className="card at-table-card">
            <div className="table-wrap">
              <table className="data-table at-table">
                <thead>
                  <tr>
                    <th className="at-col-star" title="Highlight in the app">
                      ★
                    </th>
                    <th>Task</th>
                    <th>Category</th>
                    <th className="num">Rate / hour</th>
                    <th>Status</th>
                    <th>Last disabled</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {groups.length === 0 && (
                    <tr>
                      <td colSpan={7} className="at-empty muted">
                        No tasks match in {env.name_en}.
                      </td>
                    </tr>
                  )}
                  {groups.map(({ cat, rows }) => {
                    const active = rows.filter((t) => t.is_active).length;
                    return [
                      <tr key={`g-${cat.id}`} className="at-group">
                        <td colSpan={7}>
                          <div className="at-group-head">
                            <CategoryPill cat={cat} />
                            <span className="at-group-en">
                              {cat.name_en}
                              {!cat.is_active && " · category inactive (hidden in the app)"}
                            </span>
                            <span className="at-group-sum">
                              {active} active · {rows.length - active} disabled
                            </span>
                          </div>
                        </td>
                      </tr>,
                      ...rows.map((t) => (
                        <TaskRow
                          key={t.id}
                          task={t}
                          category={catById.get(t.task_category_id)}
                          onEdit={() => setEditingId(t.id)}
                          onToast={showToast}
                        />
                      )),
                    ];
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <p className="at-legend muted small">
            <b>★</b> highlight in the app · <b>Rate / hour</b> = <code>payments_value</code> (BRL paid
            per hour) · <b>Last disabled</b> = <code>last_disable_at</code>, stamped when a task is
            disabled here
          </p>

          <AppTaskDrawer
            task={editing}
            environments={envs}
            categories={cats}
            onClose={() => setEditingId(null)}
            onSaved={showToast}
          />
          <AppTaskCreateDialog
            open={creating}
            environments={envs}
            categories={cats}
            defaultEnvSlug={env.slug}
            defaultCategoryId={catFilter}
            existingIds={existingIds}
            onClose={() => setCreating(false)}
            onCreated={showToast}
          />
          <TaskCategoriesDialog
            open={managingCats}
            environments={envs}
            categories={cats}
            defaultEnvSlug={env.slug}
            onClose={() => setManagingCats(false)}
            onToast={showToast}
          />
        </>
      )}

      <div className={toast ? "toast show" : "toast"} role="status">
        {toast}
      </div>
    </div>
  );
}
